import { useEffect, useMemo, useState } from 'react'
import textServices from 'services/text-services'
import { dlTrackEvent } from 'components/dataLayer/dataLayer'
import useRouter from 'hooks/use-router'
import {
  F1GalleryPhoto,
  F1_GALLERY_ANCHOR_ID,
  F1_GALLERY_PAGE_SIZE,
  pageCount,
  paginate
} from '../gallery'
import { F1VoteResult } from '../hooks/use-pit-stop-gallery'
import PhotoCard from './PhotoCard'
import VotePopup from './VotePopup'

interface GallerySectionProps {
  photos: F1GalleryPhoto[]
  loading: boolean
  votedIds: number[]
  isLogged: boolean
  onVote: (photo: F1GalleryPhoto) => Promise<F1VoteResult>
  onRequireLogin: () => void
  onParticipate: () => void
}

// Photo gallery (Figma 5417:10250 mobile / 5417:17541 desktop): backend
// order, 6 photos per page with arrow + dot pagination. Clicking a card
// opens the vote popup; ?photo=<code> deep links straight into it.
const GallerySection = ({
  photos,
  loading,
  votedIds,
  isLogged,
  onVote,
  onRequireLogin,
  onParticipate
}: GallerySectionProps) => {
  const { query } = useRouter()
  const [page, setPage] = useState(0)
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [deepLinkDone, setDeepLinkDone] = useState(false)

  const pages = pageCount(photos.length, F1_GALLERY_PAGE_SIZE)
  const visible = useMemo(() => paginate(photos, page, F1_GALLERY_PAGE_SIZE), [photos, page])
  // Live lookup so the popup counter follows optimistic votes/refetches
  const selected = photos.find((photo) => photo.id === selectedId) ?? null

  useEffect(() => {
    if (page > 0 && page >= pages) setPage(Math.max(pages - 1, 0))
  }, [page, pages])

  // Deep link: /pitstops?photo=<code> opens that photo's popup once
  useEffect(() => {
    if (deepLinkDone || loading) return
    const code = (query as any)?.photo
    if (code) {
      const match = photos.find((photo) => String(photo.code) === String(code))
      if (match) setSelectedId(match.id)
    }
    setDeepLinkDone(true)
  }, [deepLinkDone, loading, photos, query])

  // The no-gallery CTA shows only when there are no photos to render
  const empty = !loading && photos.length === 0

  return (
    <section id={F1_GALLERY_ANCHOR_ID} className="pitstops__gallery">
      <h2 className="pitstops__gallery-title">
        {textServices.findByText('pitstops/gallery/title', 'Galería de fotos')}
      </h2>
      <p className="pitstops__gallery-text">
        {textServices.findByText(
          'pitstops/gallery/text',
          'Vota por tu foto favorita o participa compartiendo tu foto para obtener votos y subir en el ranking. Al final el que más votos tenga ganará el abono doble al Grandstand para la F1®.'
        )}{' '}
        <strong>
          {textServices.findByText('pitstops/gallery/hint', 'Haz click en la foto para poder votarla.')}
        </strong>
      </p>

      {empty ? (
        <button
          type="button"
          className="home-v3__button pitstops__cta pitstops__gallery-cta"
          onClick={() => {
            dlTrackEvent('user_interaction', {
              action: 'click',
              element: 'galeria :: quiero participar',
              section: 'pitstops'
            })
            onParticipate()
          }}
        >
          {textServices.findByText('pitstops/gallery/emptyCta', '¡Quiero participar!')}
        </button>
      ) : (
        <>
          <div className="pitstops__gallery-grid">
            {visible.map((photo) => (
              <PhotoCard key={photo.id} photo={photo} onOpen={(item) => setSelectedId(item.id)} />
            ))}
          </div>
          {pages > 1 && (
            <div className="pitstops__gallery-pager">
              <button
                type="button"
                className="pitstops__gallery-arrow"
                aria-label="Página anterior"
                disabled={page === 0}
                onClick={() => setPage((current) => Math.max(current - 1, 0))}
              >
                ‹
              </button>
              {pages <= 8 ? (
                <div className="pitstops__gallery-dots">
                  {Array.from({ length: pages }, (_, index) => (
                    <span
                      key={index}
                      className={`pitstops__gallery-dot${index === page ? ' pitstops__gallery-dot--active' : ''}`}
                    />
                  ))}
                </div>
              ) : (
                // The Figma dots only contemplate a handful of pages; with a
                // real rally (180+ photos) they overflow, so large galleries
                // fall back to a compact page counter.
                <span className="pitstops__gallery-page-label">
                  {page + 1} / {pages}
                </span>
              )}
              <button
                type="button"
                className="pitstops__gallery-arrow"
                aria-label="Página siguiente"
                disabled={page >= pages - 1}
                onClick={() => setPage((current) => Math.min(current + 1, pages - 1))}
              >
                ›
              </button>
            </div>
          )}
        </>
      )}

      {selected && (
        <VotePopup
          photo={selected}
          voted={votedIds.includes(selected.id)}
          isLogged={isLogged}
          onVote={onVote}
          onRequireLogin={onRequireLogin}
          onClose={() => setSelectedId(null)}
        />
      )}
    </section>
  )
}

export default GallerySection
